import React from 'react';
import { ToastContainer, Toast } from 'react-bootstrap';
import { AiOutlineClose } from 'react-icons/ai';
import './BayToast.scss';

interface ToastData {
	text?: string;
	show?: boolean;
	onClose?: () => void;
	className?: string;
	variant?: string;
	externalLinkText?: string;
	onExternalLinkClick?: () => void;
	delay?: number;
	autoHide?: boolean;
	dismissible?: boolean;
	addLineBreak?: boolean;
}

interface BayToastProps {
	text?: string;
	show?: boolean;
	onClose?: () => void;
	className?: string;
	variant?: string;
	position?: 'top-start' | 'top-center' | 'top-end' | 'middle-start' | 'middle-center' | 'middle-end' | 'bottom-start' | 'bottom-center' | 'bottom-end';
	externalLinkText?: string;
	onExternalLinkClick?: () => void;
	delay?: number;
	autoHide?: boolean;
	dismissible?: boolean;
	addLineBreak?: boolean;
	toasts?: ToastData[];
	style?: React.CSSProperties;
}

/**
 *
 * @deprecated use useToast hook instead
 * @example
 * const { showToast, hideToast } = useToast()
 */
const BayToast: React.FC<BayToastProps> = ({
	text = 'Bay toast',
	show,
	onClose,
	className,
	variant = 'default',
	position = 'top-end',
	externalLinkText,
	onExternalLinkClick,
	delay = 5000,
	autoHide = true,
	dismissible = true,
	addLineBreak = false,
	toasts = [],
	style = {},
}) => {
	const renderToast = (toastData: ToastData, index: number) => {
		const {
			text: toastText = text,
			show: toastShow = show,
			onClose: toastOnClose = onClose,
			className: toastClassName = className,
			externalLinkText: toastExternalLinkText = externalLinkText,
			onExternalLinkClick: toastOnExternalLinkClick = onExternalLinkClick,
			delay: toastDelay = delay,
			autoHide: toastAutoHide = autoHide,
			dismissible: toastDismissible = dismissible,
			addLineBreak: toastAddLineBreak = addLineBreak,
		} = toastData;

		return (
			<Toast
				key={index}
				animation
				autohide={toastAutoHide}
				bsPrefix="toast"
				className={toastClassName ? toastClassName : ''}
				delay={toastDelay}
				show={toastShow}
				onClose={toastOnClose}
				onExited={toastOnClose}
			>
				<Toast.Body bsPrefix="toast-body" className="fs-6">
					<div className="d-flex justify-content-between px-2">
						<div className="d-flex justify-content-center align-items-center text-wrap fs-6">
							<p className="mb-0">
								{toastText}
								{toastExternalLinkText && (
									<>
										{toastAddLineBreak && <br />}
										<span
											className={`text-decoration-underline cursor-pointer ${
												toastAddLineBreak ? false : 'mx-1'
											}`}
											style={{ textUnderlinePosition: 'under' }}
											onClick={toastOnExternalLinkClick}
										>
											{toastExternalLinkText}
										</span>
									</>
								)}
							</p>
						</div>
						{toastDismissible && (
							<div className="cursor-pointer" onClick={toastOnClose}>
								<AiOutlineClose size={18} />
							</div>
						)}
					</div>
				</Toast.Body>
			</Toast>
		);
	};

	return (
		<ToastContainer
			bsPrefix="toast-container"
			className={`position-fixed rounded p-3 bay-toast-${variant}`}
			position={position}
			style={style}
		>
			{/* NOTE: Added new logic to handle multiple toasts. If the `toasts` prop is empty, it falls back to the existing flow. */}
			{toasts.length > 0 ? (
				toasts.map((toast, index) => renderToast(toast, index))
			) : (
				renderToast({
					text,
					show,
					onClose,
					className,
					variant,
					externalLinkText,
					onExternalLinkClick,
					delay,
					autoHide,
					dismissible,
					addLineBreak,
				}, 0)
			)}
		</ToastContainer>
	);
};

export default BayToast;
